import * as React from "react";
import {
    gql,
    useMutation
} from "@apollo/client";
import {
    CreateUserPointExchangeMutationMutation,
    CreateUserPointExchangeMutationMutationVariables
} from "../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../utils/normalizeErrors";

export const createUserPointExchangeMutation = gql`
  mutation CreateUserPointExchangeMutation($communityId: String!, $point: Int!) {
    createUserPointExchange(communityId: $communityId, point: $point) {
      errors {
        path
        message
      }
      message
      data {
        id
        communityId
        communityName
        point
        rate
        exchangedPoint
        createdAt
      }
    }
  }
`;

export type UserPointExchange = NonNullable<
  NonNullable<CreateUserPointExchangeMutationMutation["createUserPointExchange"]>["data"]
>;

export interface CreateUserPointExchangeResult {
  errors: NormalizedErrorMap | null;
  message?: string;
  data?: UserPointExchange;
  supportMeta?: {
    code?: string;
    supportRef?: string;
  };
}

export function useCreateUserPointExchange() {
  const [mutate, { loading, error }] = useMutation<
    CreateUserPointExchangeMutationMutation,
    CreateUserPointExchangeMutationMutationVariables
  >(createUserPointExchangeMutation, {
    // An exchange credits points, so refresh the balance and history if they're on screen.
    refetchQueries: ["PointsCardQuery", "EarnedPointsQuery", "FindAllUserPointExchangeHistoryQuery"],
  });

  const createUserPointExchange = React.useCallback(
    async (
      variables: CreateUserPointExchangeMutationMutationVariables
    ): Promise<CreateUserPointExchangeResult> => {
      try {
        const response = await mutate({ variables });
        const result = response.data?.createUserPointExchange;

        if (!result) {
          return { errors: { general: "No response from server." } };
        }

        return {
          errors: result.errors ? normalizeErrors(result.errors) : null,
          message: result.message ?? undefined,
          data: result.data ?? undefined,
        };
      } catch (err) {
        // Most failures (no exchange community, insufficient balance, CBM unavailable) are thrown, not returned.
        const maybeApolloErr = err as {
          message?: string;
          graphQLErrors?: Array<{ message?: string; extensions?: { code?: string; supportRef?: string } }>;
        };

        const firstGraphQLError = maybeApolloErr.graphQLErrors?.[0];
        const code = firstGraphQLError?.extensions?.code;
        const supportRef = firstGraphQLError?.extensions?.supportRef;
        const message =
          firstGraphQLError?.message ||
          maybeApolloErr.message ||
          "Unable to exchange points right now. Please try again.";

        return {
          errors: { general: message },
          supportMeta: {
            ...(typeof code === "string" ? { code } : {}),
            ...(typeof supportRef === "string" ? { supportRef } : {}),
          },
        };
      }
    },
    [mutate]
  );

  return { createUserPointExchange, loading, error };
}
